import "../../css/Login.css";
import {
  memo,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useTheme } from "../../hooks/useTheme";
import { Colors } from "../../utils/colors";
import { isDesktop } from "react-device-detect";
import {
  LoginState,
  accountActions,
  changeFullName,
  fetchOnboardingFlows,
  finishInitialStartup,
  getAccountState,
  logoutAllUsers,
  logoutSingleUser,
} from "../../redux/slices/accountSlice";
import { useDispatch, useSelector } from "react-redux";
import { MixpanelContext } from "../../context/AnalyticsService";
import { Icon } from "@iconify/react";
import { useLocation } from "react-router-dom";
import RectangleButton from "../../components/Buttons/RectangleButton";
import { DataLoaders } from "../../redux/slices/dataSlice";
import { useNavigate } from "../../hooks/useNavigate";
import LoginOnboardingProfile from "./LoginOnboardingProfile";
import LoginOnboardingAccountDetails from "./LoginOnboardingAccountDetails";
import { isGoogleAuthUser } from "../../utils/ReauthenticateLogin";
import { optInToWeeklyCommunityUpdates } from "../../utils/weeklyUpdatesOptIn";
import { usePartialAccountUpdates } from "../../hooks/usePartialAccountUpdates";
import LoginOnboardingAccountType from "../../components/Login/LoginOnboardingAccountType";
import { useOnMount } from "../../hooks/useOnMount";
import LoginWelcomeBody from "../../components/Login/LoginWelcomeBody";
import OSPills from "../../components/OSPills";
import { NavigationId } from "../../navigation/AppParamList";
import SubscriptionPlansFlow from "../../components/Subscription/SubscriptionPlansFlow";
import { getShareDomain } from "../../utils/shareLink";
import LoginOnboardingTextPreview from "./LoginOnboardingTextPreview";
import { onNavigateRedirectPathWithParams } from "../../utils/navigationUtils";
import { useMultiAccountManager } from "../../hooks/useMultiAccountManager";
import { LoginStage } from "./LoginWelcome";
import LoginOnboardingCustomizeExperience from "./LoginOnboardingCustomizeExperience";
import LoginOnboardingTeamMemberWelcome from "./LoginOnboardingTeamMemberWelcome";
import { API } from "../../API";
import { OnboardingFlowType } from "@markit/common.types";

export enum OnboardingStage {
  ONBOARD = "Onboarding",
  ACCOUNT_DETAILS = "Account Details",
  PROFILE_SETUP = "Profile Setup",
  TEXT_PREVIEW = "Text Preview",
  CUSTOMIZE_EXPERIENCE = "Customize Experience",
  TEAM_MEMBER_WELCOME = "Team Member Welcome",
  CHOOSE_PLAN = "Choose Plan",
  FINISH_SETUP = "Finish Setup",
}

const LoginOnboarding = memo(function LoginOnboardingFn() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mixpanel = useContext(MixpanelContext);
  const { theme } = useTheme();
  const {
    accountData,
    currentRoleTicket,
    loggedIn,
    redirectPath,
    additionalAccountData,
  } = useSelector(getAccountState).account;
  // defaultStage: acceleratedSetup = start on profile setup
  // signUpPhoneNumber: entered on email sign up, carried through for the creator sign up Slack alert
  const { defaultStage, signUpPhoneNumber } = useLocation().state || {};

  const { switchToAdditionalAccount } = useMultiAccountManager();

  // some cases we want to start user off without asking first questions
  const [onboardingStage, setOnboardingStage] = useState<OnboardingStage>(
    defaultStage || OnboardingStage.ONBOARD
  );
  const [loading, setLoading] = useState(false);
  // Phone number entered on LoginOnboardingAccountDetails (Google sign up) -
  // carried through for the creator sign up Slack alert, same as
  // signUpPhoneNumber above for the email sign up path
  const [googleSignUpPhoneNumber, setGoogleSignUpPhoneNumber] = useState("");

  const { accountSettings, updateAccountSettings } = usePartialAccountUpdates();

  const styles = {
    itemHeader: {
      fontSize: 16,
      fontWeight: "500",
    },
    itemDescription: {
      color: Colors.GRAY2,
      marginTop: 5,
      fontSize: 12,
    },
    labelView: { marginBottom: 7 },
    labelText: { fontSize: 12, fontWeight: "600", color: Colors.BLACK },
    labelTagText: { fontSize: 12, fontWeight: "600", color: Colors.GRAY2 },
    labelTagTextError: {
      fontSize: 12,
      fontWeight: "600",
      color: theme.ErrorBorderColor.borderColor,
    },
  };

  useOnMount(() => {
    if (loggedIn !== LoginState.LOGGED_IN) {
      navigate(NavigationId.WELCOME);
      return;
    }
    // Google sign up skips LoginWelcome, so we may need to override PROFILE_SETUP defaultStage.
    if (
      isGoogleAuthUser() &&
      accountSettings.haveNotFinishedInitialStartup &&
      (!defaultStage || defaultStage === OnboardingStage.PROFILE_SETUP)
    ) {
      setOnboardingStage(OnboardingStage.ACCOUNT_DETAILS);
    } else if (!defaultStage && accountSettings.email !== "") {
      setOnboardingStage(OnboardingStage.PROFILE_SETUP);
    }
  });

  // Only shows for phone account sign ups
  // If a phone account presses creator, we need to logout of the phone account, and then redirect to the email sign up case
  const creatorOnPress = useCallback(async () => {
    if (additionalAccountData.length > 0) {
      await switchToAdditionalAccount(additionalAccountData[0].uid, true);
      dispatch(logoutSingleUser(accountData.uid, true));
    } else {
      dispatch(logoutAllUsers(true));
    }
    navigate(NavigationId.SIGN_IN, {
      state: { preLoginStage: LoginStage.EMAIL_SIGNUP },
    });
  }, [
    accountData.uid,
    additionalAccountData,
    dispatch,
    navigate,
    switchToAdditionalAccount,
  ]);

  // Only shows for phone account sign ups
  // Proceed with subscriber mode and go straight to profile setup
  const subscriberOnPress = useCallback(() => {
    setOnboardingStage(OnboardingStage.PROFILE_SETUP);
  }, []);

  // Redirect the user to the confirmation page to accept their event role
  const redirectAcceptRole = useCallback(async () => {
    // for role redeem, don't show last screen
    if (currentRoleTicket) {
      setTimeout(() => {
        window.location.replace(
          `${getShareDomain()}/e/${currentRoleTicket.eventId}/i/${
            accountSettings.uid
          }?ticketId=${currentRoleTicket.id}`
        );
        dispatch(accountActions.setCurrentRoleTicket(undefined));
      }, 500);
    }
  }, [accountSettings.uid, currentRoleTicket, dispatch]);

  const onboardingFreeOnPress = useCallback(() => {
    if (isDesktop) {
      navigate("/home", { state: { justFinishedOnboarding: true } });
    } else {
      setOnboardingStage(OnboardingStage.FINISH_SETUP);
    }
  }, [navigate]);

  // Only shows for Google sign ups - collects what LoginWelcome's sign-up
  // form normally collects (full name, phone number + weekly-updates opt-in)
  // before continuing to the same Profile Setup step every other flow uses
  const finishAccountDetails = useCallback(
    async (phoneNumber: string) => {
      setLoading(true);
      const trimmedFullName = accountSettings.fullName.trim();
      dispatch(changeFullName(accountSettings.uid, trimmedFullName));
      await optInToWeeklyCommunityUpdates(
        accountSettings.uid,
        trimmedFullName,
        phoneNumber,
        mixpanel,
        "Sign Up"
      );
      setGoogleSignUpPhoneNumber(phoneNumber);
      setOnboardingStage(OnboardingStage.PROFILE_SETUP);
      setLoading(false);
    },
    [accountSettings.fullName, accountSettings.uid, dispatch, mixpanel]
  );

  const finishProfileSetUp = useCallback(async () => {
    setLoading(true);

    // Only call this once to avoid calling again if you revisit the profile setup screen
    if (accountSettings.haveNotFinishedInitialStartup) {
      dispatch(
        finishInitialStartup(
          accountSettings,
          mixpanel,
          signUpPhoneNumber || googleSignUpPhoneNumber
        )
      );
      updateAccountSettings({ haveNotFinishedInitialStartup: false });
    }

    if (currentRoleTicket) {
      await redirectAcceptRole();
    } else if (accountSettings.inCreatorMode) {
      if (accountSettings.teamOwnerUid) {
        // Team members get their own unique welcome step
        setOnboardingStage(OnboardingStage.TEAM_MEMBER_WELCOME);
        window.scrollTo(0, 0);
      } else {
        setOnboardingStage(OnboardingStage.TEXT_PREVIEW);
        window.scrollTo(0, 0);
      }
    } else {
      if (redirectPath) {
        onNavigateRedirectPathWithParams(navigate, dispatch, redirectPath);
      } else {
        dispatch(DataLoaders.user(accountSettings.uid));
        navigate(`/u/${accountSettings.username}`, {
          state: { username: accountSettings.username },
        });
      }
    }
    setLoading(false);
  }, [
    accountSettings,
    currentRoleTicket,
    dispatch,
    googleSignUpPhoneNumber,
    mixpanel,
    navigate,
    redirectAcceptRole,
    redirectPath,
    signUpPhoneNumber,
    updateAccountSettings,
  ]);

  const finishProfilePreview = useCallback(async () => {
    setOnboardingStage(OnboardingStage.CUSTOMIZE_EXPERIENCE);
  }, []);

  const finishCustomizeExperience = useCallback(
    async (selectedGoals: string[], selectedTypes: OnboardingFlowType[]) => {
      await API.monitor.triggerSlackAlert({
        channel: "#creator-onboarding",
        title: "Onboarding - Customize Experience",
        message: `User: ${accountSettings.username}, Selected use cases: ${selectedGoals.join(", ")}`,
      });
      if (selectedTypes.length > 0) {
        await API.profile.createOnboardingFlows({
          uid: accountSettings.uid,
          types: selectedTypes,
        });
      }
      await dispatch(fetchOnboardingFlows(accountSettings.uid));
      setOnboardingStage(OnboardingStage.CHOOSE_PLAN);
    },
    [accountSettings.username, accountSettings.uid, dispatch]
  );

  const finishTeamMemberWelcome = useCallback(() => {
    navigate("/home");
  }, [navigate]);

  const renderItemRow = useCallback(
    (
      title: string,
      description: string | ReactNode,
      showLine: boolean,
      showCheck?: boolean
    ) => (
      <div
        className="AlignedRow"
        style={{ alignItems: "flex-start", opacity: showCheck ? 0.6 : 1 }}
      >
        <div className="ColumnNormal" style={{ alignItems: "center" }}>
          <Icon
            icon={
              showCheck
                ? "ion:checkmark-circle"
                : "mdi:checkbox-blank-circle-outline"
            }
            height={16}
            style={{ color: showCheck ? Colors.GREEN2 : Colors.GRAY2 }}
          />
          {showLine ? (
            <div
              style={{
                height: 60,
                width: 1,
                backgroundColor: Colors.GRAY2,
              }}
            />
          ) : null}
        </div>
        <div
          className="ColumnNormal"
          style={{ paddingLeft: 14, width: isDesktop ? undefined : 280 }}
        >
          <span
            style={{
              ...styles.itemHeader,
              color: showCheck ? Colors.GRAY2 : Colors.BLACK,
            }}
          >
            {title}
          </span>
          <span style={styles.itemDescription}>{description}</span>
        </div>
      </div>
    ),
    [styles.itemDescription, styles.itemHeader]
  );

  // Only for mobile onboarding flow. Desktop skips this and goes straight to home.
  const renderFinishSetup = useMemo(
    () => (
      <LoginWelcomeBody
        title="You're all set!"
        subtext={`Welcome to Markit ${accountSettings.fullName}!`}
        alternateIcon={<span style={{ fontSize: 35 }}>🥳</span>}
      >
        <div className="ColumnNormal" style={{ gap: 30, width: "100%" }}>
          <div className="ColumnNormal">
            {renderItemRow(
              "Create your account",
              "You just did this! Edit your profile anytime.",
              true,
              true
            )}
            {renderItemRow(
              "Use Markit on desktop or through our mobile app for full functionality",
              <div className="ColumnNormal" style={{ gap: 14 }}>
                <span>Explore Markit and grow subscribers</span>
                <div style={{ width: 198 }}>
                  <OSPills forceLight={true} />
                </div>
              </div>,
              false,
              false
            )}
          </div>
          <RectangleButton
            buttonLabel={<span>Continue</span>}
            onPress={() => navigate("/home")}
            altColor={Colors.BLACK}
            altTextColor={Colors.WHITE}
            altPaddingVert={12}
          />
        </div>
      </LoginWelcomeBody>
    ),
    [accountSettings, renderItemRow, navigate]
  );

  return (
    <div
      className="LoginContainer"
      style={{
        height:
          onboardingStage === OnboardingStage.PROFILE_SETUP ? "auto" : "100vh",
      }}
    >
      {onboardingStage === OnboardingStage.CHOOSE_PLAN ? (
        <SubscriptionPlansFlow onboardingFreeOnPress={onboardingFreeOnPress} />
      ) : onboardingStage === OnboardingStage.TEXT_PREVIEW ? (
        <LoginOnboardingTextPreview
          accountSettings={accountSettings}
          continueOnPress={finishProfilePreview}
          backOnPress={() => setOnboardingStage(OnboardingStage.PROFILE_SETUP)}
        />
      ) : (
        <div
          className="ColumnNormal"
          style={{
            margin: isDesktop ? "auto" : undefined,
            gap: 30,
            width: isDesktop ? 544 : undefined,
            marginTop: 60,
            paddingBottom:
              onboardingStage === OnboardingStage.PROFILE_SETUP ? 100 : 0,
          }}
        >
          {onboardingStage === OnboardingStage.ACCOUNT_DETAILS ? (
            <LoginOnboardingAccountDetails
              accountSettings={accountSettings}
              updateAccountSettings={updateAccountSettings}
              loading={loading}
              onFinishSetUp={finishAccountDetails}
            />
          ) : onboardingStage === OnboardingStage.PROFILE_SETUP ? (
            <LoginOnboardingProfile
              accountSettings={accountSettings}
              updateAccountSettings={updateAccountSettings}
              loading={loading}
              onFinishSetUp={finishProfileSetUp}
            />
          ) : onboardingStage === OnboardingStage.CUSTOMIZE_EXPERIENCE ? (
            <LoginOnboardingCustomizeExperience
              continueOnPress={finishCustomizeExperience}
              backOnPress={() =>
                setOnboardingStage(OnboardingStage.TEXT_PREVIEW)
              }
            />
          ) : onboardingStage === OnboardingStage.TEAM_MEMBER_WELCOME ? (
            <LoginOnboardingTeamMemberWelcome
              teamOwnerUid={accountSettings.teamOwnerUid}
              continueOnPress={finishTeamMemberWelcome}
            />
          ) : onboardingStage === OnboardingStage.FINISH_SETUP ? (
            renderFinishSetup
          ) : (
            <LoginOnboardingAccountType
              creatorOnPress={creatorOnPress}
              subscriberOnPress={subscriberOnPress}
            />
          )}
        </div>
      )}
    </div>
  );
});

export default LoginOnboarding;
